import { useEffect, useState } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Button, EmptyState, ListContentLoader, Modal } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks/useToasts";
import {
    GoogleAdsAdGroupsStep,
    GoogleAdsAdGroupsStepHeader,
    GoogleAdsAdGroupsStepFooter,
} from "app/pages/digital-marketing/shared/google-ads/components/google-ads-ad-groups-step";
import {
    GoogleAdsCampaignsStep,
    GoogleAdsCampaignsStepHeader,
} from "app/pages/digital-marketing/shared/google-ads/components/google-ads-campaigns-step";
import {
    GoogleAdsConnectedStep,
    GoogleAdsConnectedStepHeader,
} from "app/pages/digital-marketing/shared/google-ads/components/google-ads-connected-step";
import { useGoogleAdsAccountSelection } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-account-selection";
import type { IGoogleAdsAccountOption } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-accounts";
import { useGoogleAdsAdGroupSelection } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-ad-group-selection";
import type { IGoogleAdsAdGroupListItem } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-ad-groups";
import { useGoogleAdsCampaignSelection } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-campaign-selection";
import type { IGoogleAdsCampaignListItem } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-campaigns";
import { useGoogleAdsOAuth } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-oauth";
import type { GoogleAdsImportedLinkage } from "app/services/keyword-groups/utils/googleAdsImportedKeywordGroups";

import { useCreateKeywordListFromGoogleAds } from "./hooks/use-create-keyword-list-from-google-ads";
import { useGoogleAdsAdGroupKeywords } from "./hooks/use-google-ads-ad-group-keywords";

interface IGoogleAdsImportWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onImported: (createdGroupId: string, linkage: GoogleAdsImportedLinkage) => void;
}

// Builds the Google Ads source linkage (SIM-109) from the wizard's in-memory selection state at
// the moment of a successful import - no extra API calls, just the identifiers already selected.
const buildGoogleAdsLinkage = (
    account: IGoogleAdsAccountOption,
    campaign: IGoogleAdsCampaignListItem,
    adGroup: IGoogleAdsAdGroupListItem,
    importedKeywords: string[],
): GoogleAdsImportedLinkage => ({
    customerId: account.id,
    ...(account.loginCustomerId && account.loginCustomerId !== account.id
        ? { loginCustomerId: account.loginCustomerId }
        : {}),
    campaignId: campaign.id,
    campaignName: campaign.name,
    adGroupId: adGroup.id,
    adGroupName: adGroup.name,
    adGroupResourceName: `customers/${account.id}/adGroups/${adGroup.id}`,
    // Snapshot the imported keyword texts (SIM-113) so later write actions can tell imported
    // keywords (offer "Remove") from ones added to the list afterwards (offer "Add").
    importedKeywords,
});

const WizardHeader = styled(Modal.Header)`
    padding-bottom: 16px;
`;

const WizardFooter = styled(Modal.Footer)`
    padding: 8px 24px;
    justify-content: space-between;
    align-items: center;
`;

export const GoogleAdsImportWizard = ({ isOpen, onClose, onImported }: IGoogleAdsImportWizardProps) => {
    const [importError, setImportError] = useState<string | null>(null);
    const { showErrorToast } = useToasts();

    const googleAdsOAuth = useGoogleAdsOAuth();
    const accountSelection = useGoogleAdsAccountSelection();
    const campaignSelection = useGoogleAdsCampaignSelection();
    const adGroupSelection = useGoogleAdsAdGroupSelection();

    const { isConnected } = googleAdsOAuth;
    const { selectedAccount } = accountSelection;
    const { selectedCampaign } = campaignSelection;
    const { selectedAdGroup } = adGroupSelection;

    const adGroupKeywords = useGoogleAdsAdGroupKeywords(selectedAccount, selectedAdGroup?.id ?? null);
    const createKeywordList = useCreateKeywordListFromGoogleAds();

    const { connect: connectGoogleAds, isConnecting, error: connectError } = googleAdsOAuth;
    useEffect(() => {
        if (isOpen && !isConnected && !isConnecting && !connectError) {
            void connectGoogleAds();
        }
    }, [isOpen, isConnected, isConnecting, connectError, connectGoogleAds]);

    // This wizard instance persists across opens (the parent just toggles `isOpen`), so the
    // account/campaign/ad-group selection state has to be explicitly cleared here too - otherwise
    // reopening after closing or a successful import would carry over the previous session's
    // selections. The OAuth token itself is NOT part of this - it's backed by a store shared with
    // the write flow on other pages, so a successful import shouldn't invalidate a token those
    // flows are about to reuse (only closing the wizard should).
    const resetGoogleAdsSelectionState = () => {
        accountSelection.resetAccount();
        campaignSelection.resetCampaign();
        adGroupSelection.resetAdGroup();
        setImportError(null);
    };

    const handleClose = () => {
        googleAdsOAuth.reset();
        resetGoogleAdsSelectionState();
        onClose();
    };

    const handleBackToAccounts = () => {
        accountSelection.resetAccount();
    };

    const handleBackToCampaigns = () => {
        adGroupSelection.resetAdGroup();
        campaignSelection.resetCampaign();
    };

    const handleImport = async () => {
        if (!selectedAdGroup || !selectedAccount || !selectedCampaign) {
            return;
        }

        const keywords = adGroupKeywords.data ?? [];
        setImportError(null);

        try {
            const createdGroup = await createKeywordList.mutateAsync({
                name: selectedAdGroup.name,
                keywords,
            });
            const linkage = buildGoogleAdsLinkage(selectedAccount, selectedCampaign, selectedAdGroup, keywords);
            resetGoogleAdsSelectionState();
            onImported(createdGroup.Id, linkage);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to import this ad group.";
            setImportError(message);
            showErrorToast(message);
        }
    };

    const isAdGroupKeywordsEmpty =
        Boolean(selectedAdGroup) && !adGroupKeywords.isLoading && adGroupKeywords.data?.length === 0;

    return (
        <Modal
            isOpen={isOpen}
            data-automation="kwl-google-ads-import-modal"
            customStyles={{ content: { width: "600px" } }}
        >
            <WizardHeader>
                {!isConnected ? (
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon["500"]}
                    >
                        Connect to Google Ads
                    </Text>
                ) : !selectedAccount ? (
                    <GoogleAdsConnectedStepHeader />
                ) : !selectedCampaign ? (
                    <GoogleAdsCampaignsStepHeader account={selectedAccount} />
                ) : (
                    <GoogleAdsAdGroupsStepHeader
                        account={selectedAccount}
                        campaignName={selectedCampaign.name}
                    />
                )}
                <Modal.CloseButton onClick={handleClose} />
            </WizardHeader>
            <Modal.Content
                gap="16px"
                height="max-content"
            >
                {!isConnected ? (
                    <Stack
                        gap="16px"
                        data-automation="kwl-google-ads-import-connecting"
                    >
                        {googleAdsOAuth.error ? (
                            <>
                                <Text
                                    fontVariant="body1"
                                    color={colorsPalettes.carbon["400"]}
                                >
                                    {googleAdsOAuth.error}
                                </Text>
                                <Button
                                    onClick={() => void googleAdsOAuth.connect()}
                                    data-automation="kwl-google-ads-import-retry"
                                >
                                    Try again
                                </Button>
                            </>
                        ) : (
                            <ListContentLoader loaderTitle="Connecting to Google Ads…" />
                        )}
                    </Stack>
                ) : !selectedAccount ? (
                    <GoogleAdsConnectedStep onSelectAccount={accountSelection.selectAccount} />
                ) : !selectedCampaign ? (
                    <Stack
                        gap="16px"
                        data-automation="kwl-google-ads-import-campaigns"
                    >
                        <GoogleAdsCampaignsStep
                            account={selectedAccount}
                            selectedCampaign={null}
                            onSelectCampaign={campaignSelection.selectCampaign}
                        />
                        <Button
                            type="flat"
                            onClick={handleBackToAccounts}
                            data-automation="kwl-google-ads-import-back-to-accounts"
                        >
                            Back to accounts
                        </Button>
                    </Stack>
                ) : (
                    <Stack
                        gap="16px"
                        data-automation="kwl-google-ads-import-ad-groups"
                    >
                        <GoogleAdsAdGroupsStep
                            account={selectedAccount}
                            campaignId={selectedCampaign.id}
                            selectedAdGroup={selectedAdGroup}
                            onSelectAdGroup={adGroupSelection.selectAdGroup}
                        />
                        {adGroupKeywords.isError ? (
                            <Stack
                                gap="8px"
                                alignItems="center"
                                data-automation="kwl-google-ads-import-keywords-error"
                            >
                                <EmptyState
                                    title="Couldn't load this ad group's keywords"
                                    subtitle={
                                        adGroupKeywords.error instanceof Error
                                            ? adGroupKeywords.error.message
                                            : "Unknown error"
                                    }
                                />
                                <Button
                                    type="flat"
                                    onClick={() => void adGroupKeywords.refetch()}
                                    data-automation="kwl-google-ads-import-keywords-retry"
                                >
                                    Try again
                                </Button>
                            </Stack>
                        ) : null}
                        {isAdGroupKeywordsEmpty ? (
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.red["500"]}
                                data-automation="kwl-google-ads-import-empty-ad-group"
                            >
                                This ad group has no enabled keywords to import. Pick a different ad group.
                            </Text>
                        ) : null}
                        {importError ? (
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.red["500"]}
                                data-automation="kwl-google-ads-import-error"
                            >
                                {importError}
                            </Text>
                        ) : null}
                    </Stack>
                )}
            </Modal.Content>
            {selectedCampaign ? (
                <WizardFooter>
                    <GoogleAdsAdGroupsStepFooter
                        onBack={handleBackToCampaigns}
                        onNext={() => void handleImport()}
                        isNextDisabled={
                            !selectedAdGroup || adGroupKeywords.isLoading || (adGroupKeywords.data?.length ?? 0) === 0
                        }
                        isNextLoading={createKeywordList.isPending}
                    />
                </WizardFooter>
            ) : null}
        </Modal>
    );
};
